import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import AddEditUnitForm from './unitEdit/AddEditUnitForm';
import {
  editUnit,
  bulkAssociateParentUnit,
  loadUnit,
} from '../../redux/actions/unitActions';

import Overlay from '../shared/Overlay';
import { Helmet } from 'react-helmet-async';
import { progressStepStatuses } from '../shared/progressIndicator/ProgressIndicatorItem';
import ProgressIndicator from '../shared/progressIndicator/ProgressIndicator';
import UnitCheckinPreferencesForm from './unitEdit/UnitCheckinPreferencesForm';
import {
  editUnitCheckinProfile,
  createNewUnitCheckinProfile,
} from '../../redux/actions/unitCheckinProfileActions';
import AccessControl from '../../AccessControl';
import permissions from '../../permissionsList';
import AssociatePOIForm from '../devices/alarm/AssociatePOIForm';
import { editDeviceAssociation } from '../../redux/actions/deviceActions';
import AssociateAlarmDeviceForm from '../devices/alarm/AssociateAlarmDeviceForm';
import AssociateAlertDeviceForm from '../devices/alert/AssociateAlertDeviceForm';
import { associateUsersUnit } from '../../redux/actions/userUnitActions';
import AssociateUsersToUnitForm from './unitEdit/AssociateUsersToUnitForm';
import BulkAssociateChildUnitsForm from './unitEdit/BulkEditChildUnitsForm';

/**
 * The component to add a new unit
 * @param {object} props - Props passed to this component
 * @param {object} props.location - Data from the history object used for navigation and location
 * @param {string} props.location.parent_unit - If this component was accessed from another component, this value is the value of that unit. This is used to prepopulate the form "parent unit" field
 * @param {string} props.location.previous - The url of the page that called this component. This is used for the "cancel" buttons to redirect back to where the user previously was
 * @description - This is the container for the add new unit form. This uses the overlay component and passes information down to the addnewunitform component
 * @returns {object} The contents of the form
 */
export let UnconnectedUnitEdit = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state = {}, search } = location;
  const previousRef = useRef(state.previous || '/units');
  const previous = previousRef.current;
  const { unit, checkinModes, userArray, initialUnits } = props;

  useEffect(() => {
    if (unit && unit.id) {
      props.dispatch(loadUnit(unit.id));
    }
  }, []); // eslint-disable-line

  if (!unit) {
    return null;
  }

  const searchParams = new URLSearchParams(search);
  const mode = searchParams.get('mode');
  const next = searchParams.get('next');
  const prev = searchParams.get('past');

  let nextSteps = [];

  if (next) {
    nextSteps = next.split(',');
  }
  let prevSteps = [];
  if (prev) {
    prevSteps = prev.split(',');
  }

  const formSteps = {
    type: 'units',
    nextSteps,
    prevSteps: [...prevSteps, mode],
    id: unit.id,
    previous,
  };

  function progressDisplay() {
    if (prevSteps.length > 0 || nextSteps.length > 0) {
      const prevStepsArr = prevSteps.map((item, index) => {
        if (index === 0) {
          return {
            title: item,
            first: true,
            status: progressStepStatuses.complete,
          };
        } else {
          return { title: item, status: progressStepStatuses.complete };
        }
      });
      const nextStepsArr = nextSteps.map((item, index) => {
        if (index === nextSteps.length - 1) {
          return { title: item, last: true };
        } else {
          return { title: item };
        }
      });

      let currentFirst = false;
      let currentLast = false;
      if (nextSteps.length === 0) {
        currentLast = true;
      }
      if (prevSteps.length === 0) {
        currentFirst = true;
      }

      const steps = [
        ...prevStepsArr,
        {
          status: progressStepStatuses.active,
          title: mode,
          first: currentFirst,
          last: currentLast,
        },
        ...nextStepsArr,
      ];

      return (
        <React.Fragment>
          <ProgressIndicator steps={steps} data-test="progress-bar" />
          <hr />
        </React.Fragment>
      );
    } else {
      return '';
    }
  }

  function editDisplay() {
    switch (mode) {
      case 'checkInPreferences':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.units.editUnitCheckinProfile}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="editUnit.editCheckinTitle"
                defaultMessage="Edit Unit Check-in Profile: {unit}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  unit: unit.name,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <UnitCheckinPreferencesForm
                onSubmit={editCheckinSubmit}
                previous={previous}
                data-test="edit-checkin-preferences-form"
                unit={unit}
                checkinModes={checkinModes}
              />
            </React.Fragment>
          </AccessControl>
        );
      case 'createCheckInPreferences':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.units.addUnitCheckinProfile}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="unitEdit.addCheckinTitle"
                defaultMessage="Create unit Check-in Profile: {unit}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  unit: unit.name,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <UnitCheckinPreferencesForm
                onSubmit={createCheckinSubmit}
                previous={previous}
                data-test="create-checkin-preferences-form"
                unit={unit}
                checkinModes={checkinModes}
              />
            </React.Fragment>
          </AccessControl>
        );

      case 'editPois':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.devices.editDeviceAssociation}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="unitEdit.editPOITitle"
                defaultMessage="Edit POIs for: {unit}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  unit: unit.name,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <AssociatePOIForm
                onSubmit={associateDeviceSubmit}
                previous={previous}
                data-test="edit-pois-form"
                associateId={unit.id}
                type="unit"
              />
            </React.Fragment>
          </AccessControl>
        );
      case 'associateAlarmDevice':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.devices.editDeviceAssociation}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="unitEdit.associateAlarmDevicesText"
                defaultMessage="Edit monitored devices for: {unit}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  unit: unit.name,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <AssociateAlarmDeviceForm
                onSubmit={associateDeviceSubmit}
                previous={previous}
                data-test="associate-alarm-device-form"
                associateId={unit.id}
                type="unit"
              />
            </React.Fragment>
          </AccessControl>
        );
      case 'associateAlertDevice':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.devices.editDeviceAssociation}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="unitEdit.associateAlertDevicesText"
                defaultMessage="Edit alert devices for: {unit}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  unit: unit.name,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <AssociateAlertDeviceForm
                onSubmit={associateDeviceSubmit}
                previous={previous}
                data-test="associate-alert-device-form"
                associateId={unit.id}
                type="unit"
              />
            </React.Fragment>
          </AccessControl>
        );
      case 'associateUsers':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.users.editUser}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="unitEdit.associateUsersText"
                defaultMessage="Associate users to: {unit}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  unit: unit.name,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <AssociateUsersToUnitForm
                onSubmit={associateUsersUnitSubmit}
                previous={previous}
                data-test="associate-users-unit-form"
                unit={unit.id}
                users={userArray}
                type="unit"
              />
            </React.Fragment>
          </AccessControl>
        );
      case 'associateChildUnits':
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.units.editUnit}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="unitEdit.associateChildUnitsText"
                defaultMessage="Associate child units to: {unit}"
                description="The title for the page to edit a unit"
                data-test="form-header"
                values={{
                  unit: unit.name,
                }}>
                {(txt) => (
                  <h1
                    className="no-margin text-deep-blue text-center"
                    data-test="form-header-text">
                    {txt}
                  </h1>
                )}
              </FormattedMessage>
              <hr />
              <BulkAssociateChildUnitsForm
                onSubmit={associateChildUnitsSubmit}
                previous={previous}
                data-test="associate-users-unit-form"
                unit={unit.id}
                initialUnits={initialUnits}
                type="unit"
              />
            </React.Fragment>
          </AccessControl>
        );
      default:
        return (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.units.editUnit}
            permissionMessage={true}>
            <React.Fragment>
              <FormattedMessage
                id="editUnit.editUnitTitle"
                defaultMessage="Edit Unit:"
                description="The title for the page to edit a unit">
                {(txt) => (
                  <h1 className="no-margin text-center">
                    {txt} {unit.name}
                  </h1>
                )}
              </FormattedMessage>

              <hr />

              <AddEditUnitForm
                onSubmit={editUnitSubmit}
                parent_unit={unit.parent_unit}
                previous={previous}
                data-test="edit-unit-form"
                unit={unit}
              />
            </React.Fragment>
          </AccessControl>
        );
    }
  }

  // submit functions

  const editUnitSubmit = (values) => {
    props.dispatch(editUnit(values, navigate, previous));
  };

  function editCheckinSubmit(values) {
    props.dispatch(
      editUnitCheckinProfile(values, navigate, previous, formSteps)
    );
  }

  function createCheckinSubmit(values) {
    props.dispatch(
      createNewUnitCheckinProfile(values, navigate, previous, formSteps)
    );
  }

  function associateDeviceSubmit(values) {
    const data = {
      values,
      type: 'unit',
      id: unit.id,
    };

    props.dispatch(
      editDeviceAssociation(data, navigate, previous, formSteps)
    );
  }

  const associateUsersUnitSubmit = (values) => {
    props.dispatch(
      associateUsersUnit(values, navigate, previous, formSteps)
    );
  };

  const associateChildUnitsSubmit = (values) => {
    props.dispatch(bulkAssociateParentUnit(values, navigate, previous));
  };

  return (
    <Overlay rootUrl={previous} canClose={true} data-test="edit-unit-overlay">
      <FormattedMessage
        id="unitEdit.browserTabInfo"
        defaultMessage="Units | Editing {unitName}"
        description="Done button"
        values={{
          unitName: `${unit.name}`,
        }}>
        {(txt) => (
          <Helmet>
            <title>{txt}</title>
          </Helmet>
        )}
      </FormattedMessage>
      <div className="content-container content-container--column">
        {progressDisplay()}

        {editDisplay()}
      </div>
    </Overlay>
  );
};

const mapStateToProps = (state, ownProps) => {
  const id = parseInt(ownProps.id);
  const unit = state.units.units[id];
  const usersArray = Object.values(state.users.users);
  const unitArray = Object.values(state.units.units);
  let userList = [];
  userList = usersArray.filter((user) => user && user.units && user.units.includes(id));
  const userArray = userList.map((user) => user.id);
  const unitchildren = unit ? unitArray.filter((u) => u && u.parent_unit === unit.id) : [];
  const initialUnits = unitchildren.map((u) => u.id);

  return {
    unit,
    checkinModes: state.users.checkinModes,
    userArray,
    initialUnits,
  };
};

const ConnectedUnitEdit = connect(mapStateToProps)(UnconnectedUnitEdit);

// Wrapper component to inject useParams
function UnitEdit() {
  const params = useParams();
  return <ConnectedUnitEdit id={params.id} />;
}

export default UnitEdit;
